<template>
  <div v-if="show">
    <loading :loading="loading"></loading>

    <error-alert-item
      :error-alert-event="errorAlertEvent"
      :network-error="networkError"
      @resetErrorAlertEvent="errorAlertEvent=false"
      ></error-alert-item>

    <refresh-item
      :show="showRefreshItem"
      @onRefresh="fetchInitialData"
      ></refresh-item>

    <file-upload-item
      ref="fileUploadItem"
      :allow-only-p-d-f="allowOnlyPDF"
      :allow-dismiss-without-password="true"
      @onDocumentUploaded="fetchInitialData"
      @onDocumentDeleted="fetchInitialData"
      @onDocumentCreatedOrUpdatedButPasswordNotSaved="fetchInitialData"
      ></file-upload-item>

    <div
      v-if="showUI"
      class="margin-top-20 margin-left-10 margin-right-10 margin-bottom-20">
      <weex-text
        class="font-size-sm-base"
        v-html="instruction"
        ></weex-text>

      <div v-if="bankStatementDocuments.length">
        <div
          class="margin-top-20"
          v-for="document in bankStatementDocuments"
          v-bind:key="document.id">
          <show-document-item
            :document="document"
            @onDocumentReUpload="onDocumentReUpload"
            @onDocumentDelete="onDocumentDelete"
            @onAddPassword="onAddPassword"
            ></show-document-item>
        </div>
      </div>

      <file-attach-button
        v-show="false"
        ref="reupload"
        text="Attach"
        file-attach-button-id="reupload"
        @filesAttached="reuploadDocument"
        ></file-attach-button>

      <file-attach-button
        v-if="!frontPageUploaded"
        class="margin-top-30"
        text="Bank Statement with Front Page"
        size="big"
        file-attach-button-id="upload_front_page"
        :attach-image-des-src="attachImageSrc"
        @filesAttached="uploadFrontImage"
        ></file-attach-button>

      <file-attach-button
        v-if="!uploadOnlyFrontPage"
        class="horizontal-center margin-top-40"
        text="Add Bank Statement Pages"
        size="big"
        file-attach-button-id="upload_additional"
        :attach-image-des-src="attachImageSrc"
        :enabled-btn-style="addButtonStyle"
        :enabled-text-style="{color: '#7f8c8d' }"
        @filesAttached="additionalDocumentsAttached"
        ></file-attach-button>

      <wxc-button
        text="Next"
        class="margin-top-40 margin-bottom-20 horizontal-center"
        type="blue"
        @wxcButtonClicked="onSubmit"
        ></wxc-button>
    </div>
  </div>
</template>

<script>

  import WxcButton from "../../../weex-ui/packages/wxc-button/index";
  import ShowDocumentItem from "./ShowDocumentItem"
  import Loading from "./Loading";
  import ErrorAlertItem from "./ErrorAlertItem";
  import FileAttachButton from "./FileAttachButton";
  import AlertService from "../core/alert/AlertService";
  import EzUtils from "../../utils/EzUtils";
  import LoanScopeDataHolder from "../../data/model/LoanScopeDataHolder";
  import DataSource from "../../data/DataSource";
  import RefreshItem from "./RefreshItem";
  import { EntityType } from "../../utils/EntityType";
  import FileUploadItem from "./FileUploadItem";
  import DocumentDetails, {
    DocumentDescription,
    DocumentStatus,
    DocumentStatusValueToDocumentStatus
  } from "../../utils/DocumentDetails";
  import { ImageResourcesUtil } from "../../utils/ImageResourcesUtil";

  const UPLOAD_FRONT_PAGE = "Please upload front page";
  const PROVIDE_PASSWORD = "Please provide password for required documents";

  export default {
    name: "BankStatementManualUploadItem",
    components: {
      WxcButton,
      ShowDocumentItem,
      Loading,
      ErrorAlertItem,
      FileAttachButton,
      RefreshItem,
      FileUploadItem
    },
    data: () => ({
      bankStatementDocuments: [],
      file: null,
      documentName: "",
      frontPageUploaded: false,
      anyDocumentPasswordNeeded: false,
      documentId: null,
      loading: false,
      errorAlertEvent: false,
      networkError: {},
      showUI: false,
      showRefreshItem: false
    }),

    props: {
      show: {
        type: Boolean,
        required: true,
        default: true
      },

      uploadOnlyFrontPage: {
        type: Boolean,
        default: false
      },

      allowOnlyPDF: {
        type: Boolean,
        default: true
      },

      instruction: {
        type: String,
        default: "Please upload supporting documents"
      }
    },

    methods: {
      showError(error) {
        this.loading = false;
        this.networkError = error;
        this.errorAlertEvent = true;
      },

      showToast(message) {
        AlertService.showToast(message);
      },

      onSubmit() {
        if (this.checkForValidation()) {
          this.$emit("onBankStatementsUploaded");
        }
      },

      checkForValidation() {
        if (!this.frontPageUploaded) {
          this.showToast(UPLOAD_FRONT_PAGE);
          return false;
        }

        if (this.anyDocumentPasswordNeeded) {
          this.showToast(PROVIDE_PASSWORD);
          return false;
        }

        return true;
      },

      reuploadDocument(event) {
        this.file = event.files[0];
        this.uploadDocument();
      },

      uploadFrontImage(event) {
        this.documentId = null;
        this.documentName = DocumentDescription.BANK_STATEMENT_FRONT_IMAGE.name;
        this.file = event.files[0];
        this.uploadDocument();
      },

      uploadDocument() {
        const documentDetails = DocumentDetails.getDocumentDetails(this.documentName);
        if (this.documentId) {
          this.$refs.fileUploadItem.updateDocument(
            EntityType.CLIENTS,
            LoanScopeDataHolder.getClientId(),
            this.documentId,
            this.documentName,
            documentDetails.description,
            this.file
          );
        } else {
          this.$refs.fileUploadItem.createDocument(
            EntityType.CLIENTS,
            LoanScopeDataHolder.getClientId(),
            this.documentName,
            documentDetails.description,
            documentDetails.directory,
            this.file
          );
        }
      },

      additionalDocumentsAttached(event) {
        this.documentId = null;
        this.documentName = DocumentDescription.BANK_STATEMENT.name;
        this.file = event.files[0];
        this.uploadDocument();
      },

      onDocumentReUpload(document) {
        this.documentName = document.name;
        this.documentId = document.id;
        this.$refs.reupload.attachFile();
      },

      onDocumentDelete(document) {
        this.$refs.fileUploadItem.deleteDocument(
          EntityType.CLIENTS,
          LoanScopeDataHolder.getClientId(),
          document.id
        );
      },

      onAddPassword(document) {
        const documentDetails = DocumentDetails.getDocumentDetails(document.name);
        this.$refs.fileUploadItem.askAndUpdatePassword(
          EntityType.CLIENTS,
          LoanScopeDataHolder.getClientId(),
          document.id,
          documentDetails.description,
          document.fileName
        );
      },

      fetchInitialData() {
        this.showRefreshItem = false;
        this.loading = true;
        this.anyDocumentPasswordNeeded = false;
        this.frontPageUploaded = false;
        DataSource.getDocuments(EntityType.CLIENTS, LoanScopeDataHolder.getClientId())
          .then(res => {
            this.bankStatementDocuments = [];
            this.loading = false;
            if (res.errorOccurred) {
              this.showError(res.data);
              this.showRefreshItem = true;
              this.showUI = false;
            } else {
              this.showUI = true;
              let index = 1;
              EzUtils.isNotEmpty(res.data) && res.data.forEach(document => {
                if (document
                  && (document.name === DocumentDescription.BANK_STATEMENT_FRONT_IMAGE.name
                    || document.name === DocumentDescription.BANK_STATEMENT.name)
                ) {
                  const status = document.verified ? document.verified.value : "";
                  if (status
                    && DocumentStatusValueToDocumentStatus[status] ===
                    DocumentStatus.PASSWORD_PENDING
                  ) {
                    this.anyDocumentPasswordNeeded = true;
                  }

                  if (document.name === DocumentDescription.BANK_STATEMENT_FRONT_IMAGE.name) {
                    this.frontPageUploaded = true;
                    document["displayName"] = "Bank Statement Front Page";
                    this.bankStatementDocuments.push(document);
                  } else if (!this.uploadOnlyFrontPage) {
                    document["displayName"] = "Bank Statement Page " + index;
                    index += 1;
                    this.bankStatementDocuments.push(document);
                  }
                }
              })
            }
          })
      }
    },

    computed: {
      addButtonStyle() {
        return {
          "background-color": "#f5f5f5",
          "border-color": "#7f8c8d",
          "border-width": "1px",
          "border-style": "dashed"
        }
      },

      attachImageSrc() {
        return ImageResourcesUtil.getImageLink(ImageResourcesUtil.CAMERA_ADD);
      }
    }
  }
</script>

<style
  scoped
  src="../../assets/style/base.css"></style>
